/* eslint-disable monorepo/no-internal-import */
import {parseRequestProperties} from '@aboutyou/bootstrap/src/parseRequestProperties';
import type {Locale} from '@aboutyou/config';
import {
  getHostConfigByHostname_UNSAFE,
  getVersionName,
  shopConfigByLocale,
} from '@aboutyou/config';
import {
  DD_BROWSER_LOGGERS,
  DD_ENV_NAME_MAP,
  FALLBACK_LOGS_MIDDLEWARE_ROUTE,
  FALL_LOGS_TOKEN,
  LOGS_TOKEN_HEADER,
  Log,
} from '@aboutyou/mlm';
import {FallbackLog} from '@aboutyou/mlm/src/fallbackLogsMiddleware/fallbackLogType';
import {initializeDeviceSpecificsForLogging} from '@aboutyou/utils/src/initializeDeviceSpecificsForLogging';
import type {Request, Response} from 'express';
import React from 'react';
import {renderToString} from 'react-dom/server';
import {getAppIntentUrlForDevices} from '../../../features/AppRedirect/helpers';
import {POA_CALLBACK_EVENT, POA_CALLBACK_ROUTE} from './constants';

const poaCallbackScript = () => {
  return ({
    POA_CALLBACK_EVENT,
    ordersPageRoute,
    isAppContext,
    iosDeepLink,
    androidDeepLink,
    env,
    locale,
    shop_id,
    version,
    webDevice,
    hostname,
    fallbackLogsMiddlewareRoute,
    loggerLevel,
    logsToken,
    logsTokenHeader,
  }: {
    POA_CALLBACK_EVENT: string;
    ordersPageRoute: string;
    isAppContext: boolean;
    iosDeepLink: string;
    androidDeepLink: string;
    env: string;
    locale?: Locale;
    shop_id?: number;
    version: string;
    webDevice: 'mobile-web' | 'desktop-web';
    hostname: string;
    fallbackLogsMiddlewareRoute: string;
    loggerLevel: DD_BROWSER_LOGGERS;
    logsToken: string;
    logsTokenHeader: string;
  }) => {
    const shouldRedirectToHomePage = window.location.pathname.includes('/home');

    const getDevice = (): FallbackLog['meta_data']['device'] => {
      if (!isAppContext) {
        return webDevice;
      }

      return /android/i.test(window.navigator.userAgent)
        ? 'android-app'
        : 'ios-app';
    };

    const device = getDevice();

    function bufferToHex(buffer: ArrayBuffer) {
      const bytes = new Uint8Array(buffer);
      return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }

    async function getPayloadSignature(data: Partial<FallbackLog>) {
      try {
        const encoder = new TextEncoder();
        // Import the secret key for use with HMAC-SHA256.
        const key = await crypto.subtle.importKey(
          'raw',
          encoder.encode(logsToken),
          {name: 'HMAC', hash: 'SHA-256'},
          false,
          ['sign', 'verify'],
        );

        const signatureBuffer = await crypto.subtle.sign(
          'HMAC',
          key,
          encoder.encode(JSON.stringify(data)),
        );

        // Convert the signature to a hex string.
        return bufferToHex(signatureBuffer);
      } catch (error) {
        return '';
      }
    }

    const logPoaCallback = async (
      target: 'order-detail' | 'order-overview' | 'home',
    ) => {
      try {
        const payload: Partial<FallbackLog> = {
          message: 'poa-callback',
          event: 'poa-callback',
          feat: 'bnpl',
          target,
          meta_data: {
            device,
            shop_id,
            locale,
          },
          ddtags: `env:'${env}',version:'${version}'`,
          env,
          view: {
            referrer: document.referrer,
            url: window.location.href,
            url_details: {
              host: hostname,
              path: window.location.pathname,
              queryString: Object.fromEntries(
                new URLSearchParams(window.location.search),
              ),
            },
          },
          http: {
            detected: {
              device: {
                category: device === 'desktop-web' ? 'Desktop' : 'Mobile',
                model: window.__DEVICE_DETAILS__?.deviceModel,
                brand: window.__DEVICE_DETAILS__?.browserBrands,
              },
              browser: {
                family: window.__DEVICE_DETAILS__?.browserName,
                major: window.__DEVICE_DETAILS__?.browserVersion,
              },
              os: {
                family: window.__DEVICE_DETAILS__?.osName,
                major: window.__DEVICE_DETAILS__?.osVersion,
              },
            },
            useragent: window.navigator?.userAgent,
            appVersion: window.navigator?.appVersion,
          },
          level: loggerLevel,
          logger: {name: loggerLevel},
          online: window.navigator?.onLine ?? 'unknown',
        };

        const logsSignature = await getPayloadSignature(payload);

        // keepalive so the request survives the window closing or the
        // redirect that follows right after.
        fetch(fallbackLogsMiddlewareRoute, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [logsTokenHeader]: logsSignature,
          },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } catch (error) {
        // Logging must never block the close/redirect flow.
      }
    };

    const redirectToHomePage = () => {
      window.location.href = '/';
    };

    const redirectToOrdersPage = () => {
      window.location.href = ordersPageRoute;
    };

    const redirectToAppHomePage = () => {
      window.location.href = iosDeepLink;
      window.location.href = androidDeepLink;
    };

    const redirectToAppOrdersPage = () => {
      window.location.href = iosDeepLink;
      window.location.href = androidDeepLink;
    };

    if (isAppContext) {
      // Defer the deep-link navigation until the log request has been
      // dispatched, otherwise the page may unload before fetch is called.
      if (shouldRedirectToHomePage) {
        logPoaCallback('home').finally(redirectToAppHomePage);
      } else {
        logPoaCallback('order-overview').finally(redirectToAppOrdersPage);
      }
      return;
    }

    if (shouldRedirectToHomePage) {
      const logPromise = logPoaCallback('home');

      if (window.opener) {
        window.opener.postMessage(
          {type: POA_CALLBACK_EVENT},
          window.location.origin,
        );
        // Close only after the log request has been dispatched.
        logPromise.finally(() => window.close());
      } else if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(POA_CALLBACK_EVENT);
        channel.postMessage({type: POA_CALLBACK_EVENT});
      }

      setTimeout(redirectToHomePage, 500);
    } else {
      // With an opener the popup closes and the customer lands back on the
      // Order Detail page they started from; without one we redirect to the
      // orders overview page.
      const logPromise = logPoaCallback(
        window.opener ? 'order-detail' : 'order-overview',
      );

      if (window.opener) {
        logPromise.finally(() => window.close());
      }

      setTimeout(redirectToOrdersPage, 500);
    }
  };
};

const PoAInterceptScript: React.FC<{
  ordersPageRoute: string;
  isAppContext: boolean;
  iosDeepLink: string;
  androidDeepLink: string;
  env: string;
  locale?: Locale;
  shop_id?: number;
  version: string;
  webDevice: 'mobile-web' | 'desktop-web';
  hostname: string;
}> = ({
  ordersPageRoute,
  isAppContext,
  iosDeepLink,
  androidDeepLink,
  env,
  locale,
  shop_id,
  version,
  webDevice,
  hostname,
}) => {
  return (
    <html>
      <head>
        <meta charSet="UTF-8"></meta>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        ></meta>
        {style}
      </head>
      <body>
        {/* Must run before the callback script so window.__DEVICE_DETAILS__
            is available when the poa-callback log payload is built. */}
        <script
          id="initialize-device-details-script"
          dangerouslySetInnerHTML={{
            __html: `
            (${initializeDeviceSpecificsForLogging.toString()})();
          `,
          }}
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (${poaCallbackScript().toString()})(
              {
                POA_CALLBACK_EVENT: "${POA_CALLBACK_EVENT}",
                ordersPageRoute: "${ordersPageRoute}",
                isAppContext: ${isAppContext},
                iosDeepLink: "${iosDeepLink}",
                androidDeepLink: "${androidDeepLink}",
                env: "${env}",
                locale: ${JSON.stringify(locale)},
                shop_id: ${shop_id},
                version: "${version}",
                webDevice: "${webDevice}",
                hostname: "${hostname}",
                fallbackLogsMiddlewareRoute: "${FALLBACK_LOGS_MIDDLEWARE_ROUTE}",
                loggerLevel: "${DD_BROWSER_LOGGERS.UNSAMPLED_LOGS}",
                logsToken: "${FALL_LOGS_TOKEN}",
                logsTokenHeader: "${LOGS_TOKEN_HEADER}",
              });
          `,
          }}
        ></script>
        <div className="spinner-container">
          <div className="spinner">
            <div className="spinner-ring spinner-ring-1"></div>
            <div className="spinner-ring spinner-ring-2"></div>
            <div className="spinner-ring spinner-ring-3"></div>
          </div>
        </div>
      </body>
    </html>
  );
};

const style = (
  <style>
    {`
body {
    margin: 0;
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-family: Inter, sans-serif;
    text-align: center;
    height: 75vh;
  }

  .spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    margin-top: 50px;
  }

  .spinner {
    display: inline-block;
    position: relative;
    width: 4rem;
    height: 4rem;
  }

  .spinner-ring {
    box-sizing: border-box;
    position: absolute;
    width: 51px;
    height: 51px;
    margin: 6px;
    border-radius: 50%;
    border: 4px solid #000;
    animation: spinner-frames 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #000 transparent transparent transparent;
  }

  .spinner-ring-1 {
    animation-delay: -0.45s;
  }

  .spinner-ring-2 {
    animation-delay: -0.3s;
  }

  .spinner-ring-3 {
    animation-delay: -0.15s;
  }

  @keyframes spinner-frames {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`}
  </style>
);

export const poaCallbackMiddleware = async (req: Request, res: Response) => {
  Log.transact('GET /__/poa_callback', {timestamp: String(Date.now())});

  const {
    isAppLinksSubdomainRequest: isAppContext,
    hostname,
    locale,
  } = parseRequestProperties({
    req,
  });

  // eslint-disable-next-line no-restricted-syntax
  const environment = getHostConfigByHostname_UNSAFE(hostname)?.environment;
  const env = environment ? DD_ENV_NAME_MAP[environment] : 'unknown';
  const version = getVersionName();
  const shop_id = locale ? shopConfigByLocale[locale]?.shopId : undefined;
  const webDevice: 'mobile-web' | 'desktop-web' =
    process.env.STACK === 'desktop' ? 'desktop-web' : 'mobile-web';

  const ordersPageRoute = '/a/orders';

  const iosDeepLink = isAppContext
    ? getAppIntentUrlForDevices({
        device: 'ios',
        searchParams: '',
        intent: 'a/orders',
      })
    : '';

  const androidDeepLink = isAppContext
    ? getAppIntentUrlForDevices({
        device: 'android',
        searchParams: '',
        intent: 'a/orders',
      })
    : '';

  try {
    const reactHtml = renderToString(
      <PoAInterceptScript
        ordersPageRoute={ordersPageRoute}
        isAppContext={isAppContext}
        iosDeepLink={iosDeepLink}
        androidDeepLink={androidDeepLink}
        env={env}
        locale={locale}
        shop_id={shop_id}
        version={version}
        webDevice={webDevice}
        hostname={hostname}
      />,
    );

    const fullHtml = `<!DOCTYPE html>${reactHtml}`;

    res.setHeader('Content-Type', 'text/html');

    res.status(200).send(fullHtml);
  } catch (error) {
    Log.error('@aboutyou/poaCallbackMiddleware during renderToString:', error);
    if (!res.headersSent) {
      res.status(500).send('Server Error during rendering');
    }
  }
};

poaCallbackMiddleware.PUBLIC_ROUTE = POA_CALLBACK_ROUTE;
